/**
 * Labstep
 *
 * @module grid/NewIndex/coldefs/metadata/molecule/structure
 * @desc Column Definition for metadata molecule structure
 */

import type {
  EditableCallbackParams,
  ValueGetterParams,
} from 'ag-grid-community';
import { MoleculeActionEditContainer as MoleculeActionEdit } from 'labstep-web/components/Molecule/Action/Edit';
import { MoleculeStructureContainer as MoleculeStructure } from 'labstep-web/components/Molecule/Structure';
import { ICONS } from 'labstep-web/constants/icons';
import { DataGridPlaceholder } from 'labstep-web/core/DataGrid/Placeholder';
import type {
  CellEditorParams,
  CellRendererParams,
} from 'labstep-web/core/DataGrid/types';
import {
  createOrUpdateValue,
  stopEditingAndFocus,
} from 'labstep-web/core/DataGrid/utils';
import { TextPlaceholder } from 'labstep-web/core/Text/Placeholder';
import { getMetadata } from 'labstep-web/grid/NewIndex/coldefs';
import type { NewIndexColDef } from 'labstep-web/grid/NewIndex/coldefs/types';
import { GridNewIndexColumnHeader } from 'labstep-web/grid/NewIndex/components/ColumnHeader';
import { GridNewIndexService } from 'labstep-web/grid/NewIndex/services/grid-new-index.service';
import { GridColumnDefinitionService } from 'labstep-web/grid/services/grid-column-definition.service';
import type { Entity } from 'labstep-web/models/entity.model';
import { Metadata } from 'labstep-web/models/metadata';
import type { MetadataType } from 'labstep-web/models/metadata/types';
import { MetadataThread } from 'labstep-web/models/metadata-thread.model';

export const getMetadataMoleculeStructureColDef = (
  parentEntityClass: string,
  type: MetadataType,
  metadataLabel: string,
  entityName?: string,
): NewIndexColDef => {
  return {
    colId: `metadata:${parentEntityClass}:${type}:${metadataLabel}:structure`,
    sortable: true,
    headerName: 'Structure',
    headerComponent: GridNewIndexColumnHeader,
    headerComponentParams: {
      icon: ICONS.metadata.type.molecule,
      postFilterSettings: {
        path:
          parentEntityClass !== entityName
            ? `${parentEntityClass}.metadatas`
            : 'metadatas',
      },
    },
    editable: (params: EditableCallbackParams) => {
      const entity = params.node.data;
      if (
        parentEntityClass === 'purchase_order' &&
        !entity.purchase_order
      ) {
        return false;
      }
      return GridNewIndexService.getEditable(params);
    },
    valueGetter: (params: ValueGetterParams): string | null => {
      return null;
    },
    valueSetter: (params: any): boolean => {
      return false;
    },
    cellRenderer: (params: CellRendererParams<Entity>) => {
      if (!params.node) {
        return null;
      }

      const entity = params.node.data;
      if (
        parentEntityClass === 'purchase_order' &&
        !entity.purchase_order
      ) {
        return null;
      }

      const metadata = getMetadata(
        parentEntityClass,
        params.node.data,
        metadataLabel,
        type,
      );

      const metadataThreadId =
        parentEntityClass !== params.node.data.entityName
          ? params.node.data[parentEntityClass].metadata_thread.idAttr
          : params.node.data.metadata_thread.idAttr;

      if (!metadata) {
        if (
          parentEntityClass !== params.node.data.entityName &&
          !params.node.data[parentEntityClass]
        ) {
          return false;
        }
        const props = {
          parentName: MetadataThread.entityName,
          parentId: metadataThreadId,
        };
        return (
          <TextPlaceholder
            onClick={() => {
              createOrUpdateValue({
                entityName: Metadata.entityName,
                body: {
                  type,
                  label: metadataLabel,
                  metadata_thread_id: metadataThreadId,

                  molecule: { name: 'Untitled structure' },
                },
                createProps: props,
                options:
                  GridColumnDefinitionService.getOptions(params),
              });
              return true;
            }}
          >
            Create structure
          </TextPlaceholder>
        );
      }
      // if (createProps) {
      //   return (
      //     <EntityActionCreate
      //       parentName={createProps.parentName}
      //       parentId={createProps.parentId}
      //       entityName={Metadata.entityName}
      //       actionComponentProps={{
      //         type: 'text',
      //         text: 'Draw structure',
      //         elementProps: { placeholder: true, gridCell: true },
      //       }}
      //       body={createProps.createBody}
      //       options={{
      //         onSuccess: () =>
      //           params.api.startEditingCell({
      //             rowIndex: params.rowIndex,
      //             colKey: params.column,
      //           }),
      //       }}
      //     />
      //   );
      // }
      return (
        <MoleculeStructure
          molecule={metadata.molecule}
          fallback={
            <DataGridPlaceholder
              params={params}
              children="Draw structure"
              editable={true}
            />
          }
        />
      );
    },
    cellEditorPopup: true,
    cellEditor: (params: CellEditorParams<Entity>) => {
      if (!params.node) {
        return '';
      }
      const metadata = getMetadata(
        parentEntityClass,
        params.node.data,
        metadataLabel,
        type,
      );

      if (!metadata) {
        return null;
      }

      return (
        <MoleculeActionEdit
          molecule={metadata.molecule}
          canEdit
          viewComponent={null}
          open
          onDone={(): void => stopEditingAndFocus(params)}
        />
      );
    },
  };
};
